
import { toast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';

/**
 * Send a notification through Telegram using the Supabase edge function
 * @param message The message to send
 * @param procurementId Optional procurement ID to notify users with finance role
 * @param specificRecipients Optional array of user IDs to notify
 * @returns Promise with the result of the notification attempt
 */
export const sendTelegramNotification = async (
  message: string,
  procurementId?: string,
  specificRecipients?: string[]
): Promise<{success: boolean, sent: number, failed: number}> => {
  try {
    console.log('Sending Telegram notification via edge function:', { message, procurementId, specificRecipients });
    
    const { data, error } = await supabase.functions.invoke('telegram-bot', {
      body: {
        action: 'send-notification',
        message,
        procurement_id: procurementId,
        recipient_ids: specificRecipients
      }
    });

    if (error) {
      console.error('Edge function error:', error);
      toast({
        title: "Notification Failed",
        description: error.message || "Failed to send notification",
        variant: "destructive",
      });
      return { success: false, sent: 0, failed: 0 };
    }
    
    if (data?.success) {
      toast({
        title: "Notification Sent",
        description: `Sent to ${data.sent || 0} recipients, ${data.failed || 0} failed`,
      });
    } else {
      toast({
        title: "Notification Failed",
        description: data?.error || "Failed to send notification",
        variant: "destructive",
      });
    }
    
    return {
      success: data?.success || false,
      sent: data?.sent || 0,
      failed: data?.failed || 0
    };
  } catch (error: any) {
    console.error('Failed to send Telegram notification:', error);
    toast({
      title: "Notification Error",
      description: error.message || "An unexpected error occurred",
      variant: "destructive",
    });
    return { success: false, sent: 0, failed: 0 };
  }
};

/**
 * Send an interactive message with buttons using the edge function
 */
export const sendInteractiveMessage = async (
  message: string,
  buttons: {text: string, action: string, data: string}[],
  chatId?: number,
  procurementId?: string,
  specificRecipients?: string[]
): Promise<{success: boolean, sent: number, failed: number}> => {
  try {
    console.log('Sending interactive message via edge function:', { message, buttons, chatId, procurementId, specificRecipients });
    
    const { data, error } = await supabase.functions.invoke('telegram-bot', {
      body: {
        action: 'send-interactive',
        message,
        buttons,
        chat_id: chatId,
        procurement_id: procurementId,
        recipient_ids: specificRecipients
      }
    });

    if (error) {
      console.error('Edge function error:', error);
      toast({
        title: "Message Failed",
        description: error.message || "Failed to send interactive message",
        variant: "destructive",
      });
      return { success: false, sent: 0, failed: 0 };
    }
    
    if (data?.success) {
      toast({
        title: "Interactive Message Sent",
        description: `Sent to ${data.sent || 0} recipients, ${data.failed || 0} failed`,
      });
    } else {
      toast({
        title: "Message Failed",
        description: data?.error || "Failed to send interactive message",
        variant: "destructive",
      });
    }
    
    return {
      success: data?.success || false,
      sent: data?.sent || 0,
      failed: data?.failed || 0
    };
  } catch (error: any) {
    console.error('Failed to send interactive message:', error);
    toast({
      title: "Message Error",
      description: error.message || "An unexpected error occurred",
      variant: "destructive",
    });
    return { success: false, sent: 0, failed: 0 };
  }
};
